import React, { useEffect, useRef, useState } from "react";
import { CrewPilotModuleShell } from "../CrewPilotModuleShell";
import { FEEDBACK_MESSAGE_MAX, submitFeedback } from "../../feedback/feedbackSubmission";
import {
  ChevronIcon,
  FeedbackMark,
  MailIcon,
  OtherIcon,
  PhotoIcon,
  ProblemIcon,
  SendIcon,
  SuggestionIcon,
} from "./FeedbackIcons";
import "./feedback.css";

const TYPES = [
  { value: "problem", label: "Problem", Icon: ProblemIcon },
  { value: "suggestion", label: "Suggestion", Icon: SuggestionIcon },
  { value: "other", label: "Other", Icon: OtherIcon },
];

function newFeedbackId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
    const rand = Math.floor(Math.random() * 16);
    const value = char === "x" ? rand : (rand & 0x3) | 0x8;
    return value.toString(16);
  });
}

function formatFileSize(bytes) {
  if (!Number.isFinite(bytes) || bytes < 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  const mb = bytes / (1024 * 1024);
  return `${mb >= 10 ? Math.round(mb) : mb.toFixed(1)} MB`;
}

export function FeedbackScreen({
  onBack,
  backLabel = "More",
  organizationId,
  accountEmail,
  sourceRoute,
  sourceScreen = "feedback",
  submit = submitFeedback,
}) {
  const [feedbackType, setFeedbackType] = useState("problem");
  const [message, setMessage] = useState("");
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [contactAllowed, setContactAllowed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const lock = useRef(false);
  const attemptId = useRef(null);
  const fileInput = useRef(null);

  useEffect(() => {
    if (!file) {
      setPreviewUrl("");
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  function chooseFile(next) {
    setFile(next);
    if (!next && fileInput.current) fileInput.current.value = "";
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (lock.current) return;
    lock.current = true;
    setSubmitting(true);
    setError("");
    if (!attemptId.current) attemptId.current = newFeedbackId();

    try {
      const outcome = await submit({
        feedbackId: attemptId.current,
        organizationId,
        feedbackType,
        message,
        contactAllowed,
        sourceRoute,
        sourceScreen,
        file,
      });
      setResult(outcome);
    } catch (err) {
      setError(err?.message || "Feedback could not be sent. Your message is still here — try again.");
    } finally {
      lock.current = false;
      setSubmitting(false);
    }
  }

  return (
    <CrewPilotModuleShell
      compact
      title="Send Feedback"
      subtitle="Tell Nexora what happened or what CrewPilot should improve. Your feedback helps us make CrewPilot better."
      onBack={onBack}
      backLabel={backLabel}
      layout="form"
      headerActions={(
        <span className="fb-mark" aria-hidden="true">
          <FeedbackMark size={26} />
        </span>
      )}
    >
      <div className="fb-screen">
        {result ? (
          <div className="fb-success" role="status">
            <p>Thanks for your feedback.</p>
            {result.attachmentFailed ? (
              <p>The photo could not be attached. Your message was still sent.</p>
            ) : null}
            <button type="button" className="nx-btn nx-btn-gold fb-send" onClick={onBack}>
              Back to CrewPilot
            </button>
          </div>
        ) : (
          <form className="fb-form" onSubmit={handleSubmit}>
            <fieldset className="fb-field">
              <legend className="fb-label">
                What kind of feedback is this? <span className="fb-req">*</span>
              </legend>
              <div className="fb-types" role="radiogroup" aria-label="Feedback type">
                {TYPES.map((type) => (
                  <label
                    key={type.value}
                    className={feedbackType === type.value ? "fb-type is-selected" : "fb-type"}
                  >
                    <input
                      className="fb-sr"
                      type="radio"
                      name="feedback-type"
                      value={type.value}
                      checked={feedbackType === type.value}
                      onChange={() => setFeedbackType(type.value)}
                    />
                    <type.Icon />
                    {type.label}
                  </label>
                ))}
              </div>
            </fieldset>

            <div className="fb-field">
              <div className="fb-label-row">
                <label className="fb-label" htmlFor="feedback-message">
                  Message <span className="fb-req">*</span>
                </label>
              </div>
              <textarea
                id="feedback-message"
                className="fb-message"
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                placeholder="Tell us what happened or what you'd like CrewPilot to improve."
                required
                maxLength={FEEDBACK_MESSAGE_MAX}
              />
              <span className="fb-count">{message.length}/{FEEDBACK_MESSAGE_MAX}</span>
            </div>

            <div className="fb-field">
              <span className="fb-label" id="feedback-photo-label">Screenshot or photo (optional)</span>
              <input
                ref={fileInput}
                className="fb-sr"
                type="file"
                accept="image/*"
                aria-labelledby="feedback-photo-label"
                onChange={(event) => chooseFile(event.target.files?.[0] || null)}
              />
              {file ? (
                <div className="fb-file-card">
                  {previewUrl ? (
                    <img className="fb-thumb" src={previewUrl} alt="" />
                  ) : (
                    <span className="fb-file-icon"><PhotoIcon /></span>
                  )}
                  <span className="fb-file-meta">
                    <span className="fb-file-name">{file.name}</span>
                    <span className="fb-file-size">{formatFileSize(file.size)}</span>
                  </span>
                  <button type="button" className="fb-file-remove" aria-label="Remove photo" onClick={() => chooseFile(null)}>
                    ×
                  </button>
                  <button type="button" className="fb-file-change" onClick={() => fileInput.current?.click()}>
                    Change
                  </button>
                </div>
              ) : (
                <button type="button" className="fb-file-add" onClick={() => fileInput.current?.click()}>
                  <span className="fb-file-icon"><PhotoIcon /></span>
                  <span className="fb-file-copy">
                    <span className="fb-file-title">Add screenshot or photo</span>
                    <span className="fb-file-hint">Choose an image or take a photo</span>
                  </span>
                  <ChevronIcon />
                </button>
              )}
            </div>

            <label className="fb-contact">
              <span className="fb-contact-icon" aria-hidden="true"><MailIcon /></span>
              <span className="fb-contact-copy">
                <span className="fb-contact-title">You may contact me about this feedback.</span>
                <span className="fb-contact-sub">
                  We'll use your CrewPilot account email.
                  {contactAllowed && accountEmail ? ` ${accountEmail}` : ""}
                </span>
              </span>
              <input
                className="fb-switch"
                type="checkbox"
                role="switch"
                checked={contactAllowed}
                onChange={(event) => setContactAllowed(event.target.checked)}
              />
            </label>

            {error ? <p className="fb-error" role="alert">{error}</p> : null}

            <button type="submit" className="nx-btn nx-btn-gold fb-send" disabled={submitting} aria-busy={submitting}>
              {submitting ? "Sending…" : (
                <>
                  <SendIcon />
                  Send Feedback
                </>
              )}
            </button>
          </form>
        )}
      </div>
    </CrewPilotModuleShell>
  );
}
